import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  Slider,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Popover,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  TextField,
  Alert,
} from '@mui/material';
import {
  Phone as CallIcon,
  CallEnd as HangUpIcon,
  Backspace as BackspaceIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Dialpad as DialpadIcon,
  VolumeUp as VolumeUpIcon,
  Analytics as AnalyticsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CallMade as CallMadeIcon,
  CallReceived as CallReceivedIcon,
  History as HistoryIcon,
  SwapHoriz as TransferIcon,
} from '@mui/icons-material';
import { useWebPhoneContextV4 } from '../../contexts/WebPhoneContextV4';
import { CallStatus } from '../../hooks/useWebPhoneV4';
import brandingService from '../../services/branding.service';
import api from '../../services/api';

const WEBPHONE_VERSION = '4.00.00';

interface CallHistoryItem {
  id: number;
  remote_number: string;
  direction: 'inbound' | 'outbound' | 'internal';
  start_time: string;
  duration: number;
  duration_formatted: string;
  disposition: string;
  caller_id_name?: string;
}

const WebPhoneStandaloneV4: React.FC = () => {
  const {
    connectionStatus,
    callStatus,
    currentCall,
    consultCall,
    isMuted,
    isOnHold,
    dialedNumber,
    errorMessage,
    // V3: volume, changeVolume, testSpeaker removed - no volume control
    makeCall,
    answerCall,
    hangUp,
    toggleMute,
    toggleHold,
    transferCall,
    startConsultCall,
    cancelConsultCall,
    completeAttendedTransfer,
    addDigit,
    deleteDigit,
    clearNumber,
    connect,
    // V3: rtcStats, eventLogs removed - no data collection
  } = useWebPhoneContextV4();

  const [showDialpad, setShowDialpad] = useState(true);
  const [branding, setBranding] = useState<{ logo_url: string; brand_name: string } | null>(null);
  const [historyAnchorEl, setHistoryAnchorEl] = useState<HTMLElement | null>(null);
  const [callHistory, setCallHistory] = useState<CallHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [blindTransferAnchorEl, setBlindTransferAnchorEl] = useState<HTMLElement | null>(null);
  const [blindTransferInput, setBlindTransferInput] = useState('');
  const [blindTransferError, setBlindTransferError] = useState<string | null>(null);
  const [attendedAnchorEl, setAttendedAnchorEl] = useState<HTMLElement | null>(null);
  const [attendedInput, setAttendedInput] = useState('');
  const [attendedError, setAttendedError] = useState<string | null>(null);
  const numberDisplayRef = useRef<HTMLDivElement>(null);
  const prevCallStatusRef = useRef<CallStatus>(callStatus);
  // V3: showStats removed - no stats panel

  // Load branding on mount
  useEffect(() => {
    const loadBranding = async () => {
      try {
        const brandData = await brandingService.getCurrentBranding();
        setBranding(brandData);
      } catch (error) {
        console.error('[WebPhoneV4] Failed to load branding:', error);
      }
    };
    loadBranding();
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    if (connectionStatus === 'disconnected') {
      connect();
    }
  }, []);

  useEffect(() => {
    if (callStatus === 'idle') {
      setBlindTransferAnchorEl(null);
      setBlindTransferInput('');
      setBlindTransferError(null);
      setAttendedAnchorEl(null);
      setAttendedInput('');
      setAttendedError(null);
    }
  }, [callStatus]);

  // Fetch call history
  const fetchCallHistory = async () => {
    setLoadingHistory(true);
    try {
      // Request last 30 calls regardless of date - use a 1 year lookback window
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      const startDate = oneYearAgo.toISOString().split('T')[0];
      const endDate = new Date().toISOString().split('T')[0];

      const response = await api.get('/endpoints/user-call-history-simple', {
        params: {
          per_page: 30,
          page: 1,
        },
      });
      if (response.data.success && response.data.data && Array.isArray(response.data.data.calls)) {
        setCallHistory(response.data.data.calls);
      } else {
        console.error('[WebPhoneV4] Failed to load call history or invalid data format');
        setCallHistory([]);
      }
    } catch (error) {
      console.error('[WebPhoneV4] Error loading call history:', error);
      setCallHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Handle opening call history
  const handleHistoryOpen = (event: React.MouseEvent<HTMLElement>) => {
    setHistoryAnchorEl(event.currentTarget);
    fetchCallHistory();
  };

  // Handle closing call history
  const handleHistoryClose = () => {
    setHistoryAnchorEl(null);
  };

  // Handle selecting a number from history
  const handleSelectNumber = (number: string) => {
    clearNumber();
    number.split('').forEach((digit) => addDigit(digit));
    handleHistoryClose();
  };

  // Keyboard input for number entry (PC only)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if not in an input field
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

      // Handle number keys (0-9, *, #)
      if (/^[0-9*#]$/.test(e.key)) {
        e.preventDefault();
        addDigit(e.key);
      }
      // Handle backspace
      else if (e.key === 'Backspace') {
        e.preventDefault();
        deleteDigit();
      }
      // Handle enter to make call
      else if (e.key === 'Enter' && dialedNumber && connectionStatus === 'registered' && callStatus === 'idle') {
        e.preventDefault();
        makeCall(dialedNumber);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dialedNumber, connectionStatus, callStatus, addDigit, deleteDigit, makeCall]);

  // Paste handler to extract clean number
  useEffect(() => {
    const handlePaste = (e: Event) => {
      // Only handle if not in an input field
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

      const clipboardEvent = e as ClipboardEvent;
      e.preventDefault();
      const pastedText = clipboardEvent.clipboardData?.getData('text') || '';
      // Extract only digits, *, and #
      const cleanNumber = pastedText.replace(/[^0-9*#]/g, '');
      if (cleanNumber) {
        clearNumber();
        cleanNumber.split('').forEach((digit) => addDigit(digit));
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [addDigit, clearNumber]);

  // Clear number when call ends
  useEffect(() => {
    // Check if we transitioned from 'in-call' to 'idle'
    if (
      (
        prevCallStatusRef.current === 'in-call' ||
        prevCallStatusRef.current === 'transferring' ||
        prevCallStatusRef.current === 'consulting' ||
        prevCallStatusRef.current === 'consult-connected'
      ) &&
      callStatus === 'idle'
    ) {
      clearNumber();
    }
    // Update ref with current status for next render
    prevCallStatusRef.current = callStatus;
  }, [callStatus, clearNumber]);

  // Format call duration
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get status display
  const getStatusDisplay = () => {
    if (connectionStatus === 'disconnected') return { icon: '✗', text: 'Disconnected', color: '#f44336' };
    if (connectionStatus === 'connecting') return { icon: '⏳', text: 'Connecting...', color: '#ff9800' };
    if (connectionStatus === 'connected') return { icon: '✓', text: 'Connected', color: '#4caf50' };
    if (connectionStatus === 'registered') return { icon: '✓', text: 'Registered', color: '#4caf50' };
    if (connectionStatus === 'error') return { icon: '✗', text: 'Error', color: '#f44336' };
    return { icon: '?', text: 'Unknown', color: '#9e9e9e' };
  };

  const getCallStatusDisplay = () => {
    if (callStatus === 'calling') return { text: '📞 Calling...', color: '#ff9800' };
    if (callStatus === 'ringing') return { text: '📞 Ringing...', color: '#ff9800' };
    if (callStatus === 'transferring') return { text: '🔁 Transferring...', color: '#0284c7' };
    if (callStatus === 'consulting') {
      const target = consultCall?.remoteNumber || 'extension';
      return { text: `Consulting with ${target}...`, color: '#0ea5e9' };
    }
    if (callStatus === 'consult-connected') {
      const target = consultCall?.remoteNumber || 'extension';
      return { text: `Consult call with ${target}`, color: '#0ea5e9' };
    }
    if (callStatus === 'in-call' && currentCall) {
      const direction = currentCall.direction === 'outgoing' ? 'to' : 'from';
      const number = currentCall.remoteNumber;
      return { text: `Active Call ${direction} ${number}`, color: '#4caf50' };
    }
    return null;
  };

  const status = getStatusDisplay();
  const callStatusInfo = getCallStatusDisplay();
  const isTransferring = callStatus === 'transferring';
  const isConsulting = callStatus === 'consulting' || callStatus === 'consult-connected';
  const isActiveCall = callStatus === 'in-call' || callStatus === 'transferring' || isConsulting;

  // Dialpad keys
  const dialpadKeys = [
    { digit: '1', letters: '' },
    { digit: '2', letters: 'ABC' },
    { digit: '3', letters: 'DEF' },
    { digit: '4', letters: 'GHI' },
    { digit: '5', letters: 'JKL' },
    { digit: '6', letters: 'MNO' },
    { digit: '7', letters: 'PQRS' },
    { digit: '8', letters: 'TUV' },
    { digit: '9', letters: 'WXYZ' },
    { digit: '*', letters: '' },
    { digit: '0', letters: '+' },
    { digit: '#', letters: '' },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      {/* Header - Branded Banner */}
      <Box
        sx={{
          background: 'white',
          color: '#333',
          p: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        {branding?.logo_url ? (
          <img
            src={branding.logo_url}
            alt={branding.brand_name || 'Logo'}
            style={{
              height: '45px',
              width: 'auto',
              display: 'block',
            }}
          />
        ) : (
          <>
            <CallIcon sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              WebPhone
            </Typography>
          </>
        )}
      </Box>

      {/* Body */}
      <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
        {/* Status Display */}
        <Chip
          label={
            callStatusInfo
              ? callStatusInfo.text
              : `${status.icon} v${WEBPHONE_VERSION} - ${status.text}`
          }
          size="small"
          sx={{
            width: '100%',
            mb: 2,
            fontWeight: 600,
            bgcolor: callStatusInfo ? callStatusInfo.color : status.color,
            color: 'white',
          }}
        />

        {/* Error Message */}
        {errorMessage && (
          <Box sx={{ mb: 2, p: 1, bgcolor: '#fee', borderRadius: 1 }}>
            <Typography variant="body2" color="error">
              {errorMessage}
            </Typography>
          </Box>
        )}


        {/* Call Info (when in call) */}
        {currentCall && (
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {currentCall.remoteNumber}
            </Typography>
            {currentCall.status === 'connected' && (
              <Typography variant="body2" color="text.secondary">
                {formatDuration(currentCall.duration)}
              </Typography>
            )}
            {isTransferring && (
              <Typography variant="body2" color="text.secondary">
                Transferring call...
              </Typography>
            )}
          </Box>
        )}
        {isConsulting && consultCall && (
          <Box sx={{ mb: 2, textAlign: 'center', p: 1.5, border: '1px dashed #0ea5e9', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0ea5e9' }}>
              Consult: {consultCall.remoteNumber}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {callStatus === 'consulting' ? 'Ringing...' : 'Connected'}
            </Typography>
          </Box>
        )}

        {/* Number Display with History */}
        <Box sx={{ mb: 2, position: 'relative' }}>
          <Paper
            ref={numberDisplayRef}
            variant="outlined"
            onClick={handleHistoryOpen}
            sx={{
              p: 2,
              minHeight: 60,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: '#f8f9fa',
              borderColor: '#e9ecef',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: '#e9ecef',
              },
            }}
          >
            <IconButton size="small" sx={{ color: '#666' }}>
              <HistoryIcon />
            </IconButton>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: dialedNumber ? '#333' : '#adb5bd',
                wordBreak: 'break-all',
                flex: 1,
                textAlign: 'right',
              }}
            >
              {dialedNumber || 'Enter number'}
            </Typography>
          </Paper>

          {/* Call History Popover */}
          <Popover
            open={Boolean(historyAnchorEl)}
            anchorEl={historyAnchorEl}
            onClose={handleHistoryClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            sx={{
              mt: 1,
            }}
            slotProps={{
              paper: {
                sx: { minWidth: 300, maxWidth: 400 },
              },
            }}
          >
            {loadingHistory ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : callHistory.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No recent calls
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {callHistory.map((call) => (
                  <ListItemButton
                    key={call.id}
                    onClick={() => handleSelectNumber(call.remote_number)}
                    sx={{
                      borderBottom: '1px solid #e0e0e0',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <ListItemIcon>
                      {call.direction === 'outbound' ? (
                        <CallMadeIcon color="primary" />
                      ) : (
                        <CallReceivedIcon color="success" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={call.remote_number}
                      secondary={
                        <>
                          {call.caller_id_name && <span>{call.caller_id_name} • </span>}
                          {call.duration_formatted} • {new Date(call.start_time).toLocaleString()}
                        </>
                      }
                      primaryTypographyProps={{
                        fontWeight: 600,
                        fontSize: '0.95rem',
                      }}
                      secondaryTypographyProps={{
                        fontSize: '0.75rem',
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Popover>
        </Box>

        {/* Dialpad (show only when not in call or if toggled) */}
        {(callStatus === 'idle' || showDialpad) && (
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {dialpadKeys.map((key) => (
              <Grid item xs={4} key={key.digit}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => addDigit(key.digit)}
                  sx={{
                    height: 60,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    bgcolor: '#f8f9fa',
                    borderColor: '#e9ecef',
                    '&:hover': {
                      bgcolor: '#e9ecef',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
                    {key.digit}
                  </Typography>
                  {key.letters && (
                    <Typography variant="caption" sx={{ color: '#666', fontSize: 10 }}>
                      {key.letters}
                    </Typography>
                  )}
                </Button>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Action Buttons */}
        <Grid container spacing={1}>
          {callStatus === 'idle' && (
            <>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CallIcon />}
                  onClick={() => makeCall(dialedNumber)}
                  disabled={connectionStatus !== 'registered' || !dialedNumber || callStatus !== 'idle'}
                  sx={{
                    bgcolor: '#4caf50',
                    '&:hover': { bgcolor: '#45a049' },
                  }}
                >
                  Call
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<BackspaceIcon />}
                  onClick={deleteDigit}
                  disabled={!dialedNumber}
                >
                  Delete
                </Button>
              </Grid>
            </>
          )}

          {callStatus === 'calling' && (
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<HangUpIcon />}
                onClick={hangUp}
                sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#da190b' } }}
              >
                Cancel
              </Button>
            </Grid>
          )}

          {callStatus === 'ringing' && currentCall?.direction === 'outgoing' && (
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<HangUpIcon />}
                onClick={hangUp}
                sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#da190b' } }}
              >
                Cancel
              </Button>
            </Grid>
          )}

          {callStatus === 'ringing' && currentCall?.direction === 'incoming' && (
            <>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CallIcon />}
                  onClick={answerCall}
                  sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#45a049' } }}
                >
                  Answer
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<HangUpIcon />}
                  onClick={hangUp}
                  sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#da190b' } }}
                >
                  Decline
                </Button>
              </Grid>
            </>
          )}

          {isActiveCall && (
            <>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={isMuted ? <MicOffIcon /> : <MicIcon />}
                  onClick={toggleMute}
                  disabled={isTransferring}
                  sx={{
                    bgcolor: isMuted ? '#ff9800' : 'transparent',
                    color: isMuted ? 'white' : 'inherit',
                  }}
                >
                  {isMuted ? 'Unmute' : 'Mute'}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={isOnHold ? <PlayIcon /> : <PauseIcon />}
                  onClick={toggleHold}
                  disabled={isTransferring || isConsulting}
                  sx={{
                    bgcolor: isOnHold ? '#9c27b0' : 'transparent',
                    color: isOnHold ? 'white' : 'inherit',
                  }}
                >
                  {isOnHold ? 'Resume' : 'Hold'}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<DialpadIcon />}
                  onClick={() => setShowDialpad(!showDialpad)}
                  disabled={isTransferring}
                >
                  {showDialpad ? 'Hide' : 'Show'} Keys
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<HangUpIcon />}
                  onClick={hangUp}
                  sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#da190b' } }}
                >
                  Hang Up
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={
                    isTransferring ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <TransferIcon />
                  }
                  onClick={(event) => {
                    setBlindTransferAnchorEl(event.currentTarget);
                    setBlindTransferError(null);
                  }}
                  disabled={isTransferring || isConsulting}
                >
                  {isTransferring ? 'Transferring...' : 'Blind Transfer'}
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<TransferIcon />}
                  onClick={(event) => {
                    setAttendedAnchorEl(event.currentTarget);
                    setAttendedError(null);
                  }}
                  disabled={isTransferring || isConsulting}
                >
                  Attended Transfer
                </Button>
              </Grid>
              {isConsulting && (
                <>
                  <Grid item xs={6}>
                    <Button fullWidth variant="text" onClick={cancelConsultCall}>
                      Cancel Consult
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={completeAttendedTransfer}
                      disabled={callStatus !== 'consult-connected'}
                    >
                      Complete Transfer
                    </Button>
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>

        <Popover
          open={Boolean(blindTransferAnchorEl)}
          anchorEl={blindTransferAnchorEl}
          onClose={() => {
            if (!isTransferring) {
              setBlindTransferAnchorEl(null);
              setBlindTransferInput('');
              setBlindTransferError(null);
            }
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          sx={{ mt: 1 }}
        >
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              const cleaned = blindTransferInput.replace(/[^0-9*#]/g, '');
              if (!cleaned) {
                setBlindTransferError('Enter a valid extension (numbers, *, or #).');
                return;
              }
              setBlindTransferError(null);
              transferCall(cleaned);
            }}
            sx={{ p: 2, width: 280 }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Transfer Call
            </Typography>
            <TextField
              label="Target Extension"
              value={blindTransferInput}
              onChange={(e) => setBlindTransferInput(e.target.value)}
              placeholder="e.g. 1234"
              autoFocus
              fullWidth
              size="small"
              disabled={isTransferring}
            />
            {blindTransferError && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {blindTransferError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="text"
                onClick={() => {
                  setBlindTransferAnchorEl(null);
                  setBlindTransferInput('');
                  setBlindTransferError(null);
                }}
                disabled={isTransferring}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={isTransferring}>
                Blind Transfer
              </Button>
            </Box>
          </Box>
        </Popover>

        <Popover
          open={Boolean(attendedAnchorEl)}
          anchorEl={attendedAnchorEl}
          onClose={() => {
            if (!isConsulting && !isTransferring) {
              setAttendedAnchorEl(null);
              setAttendedInput('');
              setAttendedError(null);
            }
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          sx={{ mt: 1 }}
        >
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              const cleaned = attendedInput.replace(/[^0-9*#]/g, '');
              if (!cleaned) {
                setAttendedError('Enter a valid extension (numbers, *, or #).');
                return;
              }
              setAttendedError(null);
              setAttendedAnchorEl(null);
              setAttendedInput('');
              startConsultCall(cleaned);
            }}
            sx={{ p: 2, width: 280 }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Start Attended Transfer
            </Typography>
            <TextField
              label="Target Extension"
              value={attendedInput}
              onChange={(e) => setAttendedInput(e.target.value)}
              placeholder="e.g. 1234"
              autoFocus
              fullWidth
              size="small"
              disabled={isConsulting}
            />
            {attendedError && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {attendedError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="text"
                onClick={() => {
                  setAttendedAnchorEl(null);
                  setAttendedInput('');
                  setAttendedError(null);
                }}
                disabled={isConsulting}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={isConsulting}>
                Start Consult
              </Button>
            </Box>
          </Box>
        </Popover>
      </Box>
    </Box>
  );
};

export default WebPhoneStandaloneV4;
